import { Button, styled, Typography } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import { Disclosure } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import ManualKycDetailsForm from './ManualKycDetailsForm';
import { kycRiskRatingLabels } from 'further-ui/labels';
import { KycRiskRating } from 'further-types/kyc';

const kycRiskRatingLabelsMap = Object.fromEntries(
  kycRiskRatingLabels.map((label) => [label.value, label.label]),
);

const KycButton = styled(Button)(({ theme }) => ({
  padding: 0,
  color: theme.palette.grey[600],
  textDecoration: 'underline',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
  minWidth: 'auto',
}));

type Props = {
  vehicleId: string;
  investorId: string;
  vehicleApplicationId: string;
  isInformationCompleted: boolean;
  manualKycDisclosure: Disclosure;
};

const KybDetails: React.FC<Props> = ({
  vehicleId,
  investorId,
  vehicleApplicationId,
  isInformationCompleted,
  manualKycDisclosure,
}) => {
  const [kybDetails] = api.lpKyc.getKybDetails.useSuspenseQuery({
    vehicleId,
    vehicleApplicationId,
  });

  const [investorKyc] = api.lpKyc.getInvestorKyc.useSuspenseQuery({
    vehicleId,
    investorId,
  });

  const files = kybDetails?.answers.flatMap((answer) => answer.answerFiles);
  const manualApprovalFiles = investorKyc?.manualApprovalFiles;

  const handleOpenManualKycDetailsForm = () => {
    manualKycDisclosure.onOpen();
  };

  return (
    <>
      <FormField label="Written responses" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {isInformationCompleted ? 'Completed' : 'In progress'}
        </Typography>
      </FormField>
      <FormField label="User-uploaded files" spacing="xxs">
        {files?.map((file) => (
          <Row spacing="xs" key={file.path}>
            <Typography variant="body2" color="grey.600">
              {file.name}
            </Typography>
            <KycButton variant="text" color="primary" href={file.previewUrl}>
              Download
            </KycButton>
          </Row>
        )) ?? (
          <Typography variant="body2" color="grey.600">
            --
          </Typography>
        )}
      </FormField>
      <FormField label="Risk rating" spacing="xs">
        <Row spacing="xxs" centered>
          <Typography variant="body2" color="grey.600">
            {investorKyc?.riskRating
              ? kycRiskRatingLabelsMap[investorKyc.riskRating]
              : kycRiskRatingLabelsMap[KycRiskRating.Low]}
          </Typography>
          <KycButton
            variant="text"
            color="primary"
            onClick={handleOpenManualKycDetailsForm}
          >
            Edit
          </KycButton>
        </Row>
      </FormField>
      {manualApprovalFiles?.length ? (
        <FormField label="Admin-uploaded files" spacing="xxs">
          {manualApprovalFiles.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <KycButton variant="text" color="primary" href={file.previewUrl}>
                Download
              </KycButton>
            </Row>
          ))}
        </FormField>
      ) : null}

      <FormField label="Notes" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {investorKyc?.manualApprovalNotes ?? '--'}
        </Typography>
        <KycButton
          variant="text"
          color="primary"
          onClick={handleOpenManualKycDetailsForm}
        >
          Edit notes
        </KycButton>
      </FormField>
      {manualKycDisclosure.isOpen ? (
        <ManualKycDetailsForm
          title="Manual KYB record"
          investorKyc={investorKyc ?? undefined}
          vehicleId={vehicleId}
          investorId={investorId}
          onClose={manualKycDisclosure.onClose}
        />
      ) : null}
    </>
  );
};

export default KybDetails;
